import { BUSINESS_PLAN_TYPES, IBusinessToolType } from 'constants/businessPlan';
import { t } from 'i18next';
import { omit } from 'lodash';
import {
  IChartDatasetItem,
  IChartWithBars,
  IChartWithStackedBars,
  IDonutWithText,
  ILinesChart,
  IMixedChart,
  IMixedChartDatasetItem,
} from 'PDF/components/Charts/types';
import { IFinBlockTableProps, IFinBlockTableRowItem } from 'PDF/components/FinBlockTable';
import { RowType } from 'PDF/components/FinBlockTable/FinBlockTable';
import { getColorStyles, getColorStylesByRecord, getColorType } from 'PDF/helpers/documentStyle';
import { convertToNumber, getMagazineTextObject } from 'PDF/helpers/pdfMapping';
import { shouldRenderFinancialData } from 'PDF/helpers/shouldRenderFinancialData';

import { IPeriodNames, IPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';
import {
  FUNDING_DIRECTIONS_NAMES,
  IFundingDirectionsFormValuesType,
} from 'components/BusinessToolForm/Sections/FinancialPlan/CashFlowElements/Sections/FundingDirectionsForm/utils/form';
import { personnelExpensesColumns } from 'components/BusinessToolForm/Sections/FinancialPlan/PlannedExpenses/PersonnelExpensesForm/utils/form';
import { IRow } from 'components/BusinessToolForm/Sections/FinancialPlan/PlannedExpenses/SalesAndMarketingForm/utils/form';
import {
  CURRENT_ASSETS_TABLE_TOTAL_NAMES,
  NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES,
} from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedBalanceSheets/Sections/Assets/utils/form';
import {
  CURRENT_LIABILITIES_TABLE_NAMES,
  EQUITY_TABLE_NAMES,
  NON_CURRENT_LIABILITIES_TABLE_NAMES,
} from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedBalanceSheets/Sections/LiabilitiesAndEquity/utils/form';
import { adjustmentsForLinkedFields } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/InDirectForm/InDirectFormComponents/Sections/AdjustmentsForForm/utils/form';
import { operatingAssetsAndLiabilitiesLinkedFields } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/InDirectForm/InDirectFormComponents/Sections/OperatingAssetsAndLiabilitiesForm/utils/form';
import {
  getFinancingActivitiesRowsTitles,
  getInvestingActivitiesRowsTitles,
  operatingActivitiesRows,
} from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/utils/form';
import { IBusinessToolForm } from 'components/BusinessToolForm/utils/form';

import { getYearsPeriod } from 'helpers/formatPeriods';
import { getCurrencySymbol } from 'helpers/getCurrencySymbol';
import { getTranslatedInitialValue } from 'helpers/getTranslatedInitialValue';
import { getNumberFromString } from 'helpers/numbers';
import { formatNumber, formatNumberToShort } from 'helpers/numbers';

import {
  colorIndexesCashFlow,
  colorIndexescostPerUnitChart,
  colorIndexesFixedExpenses,
  colorIndexesFundingDirections,
  colorIndexesGeneralExpenses,
  colorIndexesLoanRepaymentSchedule,
  colorIndexesMarketingExpenses,
  colorIndexesPlannedExpenses,
  colorIndexesProfitAndLoss,
  colorIndexesProjectedBalance,
  colorIndexesProjectFinancing,
  colorIndexesRAndD,
  colorIndexesVariableExpenses,
  colorsIndexesEfficiency,
  colorsIndexesLineChart,
  colorsIndexesLiquidity,
  colorsIndexesRatios,
  colorsIndexesSolvencyRatios,
} from './chartColors';
import { getDimensionalityForExamplePage } from '../MarketAnalysis/helpers';

const getPeriodValues = (row: IPeriodsTableRow, period: IPeriodNames) =>
  Object.values(row[period]).map(convertToNumber);

const getChartDatasetItems = (
  variants: string[],
  table: IPeriodsTableRow[],
  period: IPeriodNames,
  isExamplePage: boolean,
) => {
  const datasets = variants.reduce<IChartDatasetItem[]>((result, variant, index) => {
    const data = getPeriodValues(table[index], period);
    if (data.some(Boolean)) {
      result.push({
        label: isExamplePage ? variant : getTranslatedInitialValue(variant),
        data,
      });
    }
    return result;
  }, []);

  return datasets;
};

export const getMagazineItems = (
  businessPlanChapters: IBusinessToolForm,
  businessPlanType: IBusinessToolType,
  isExamplePage: boolean,
) => {
  const {
    expectedRevenueVolume,
    increaseFactors,
    changeInPrices,
    seasonalityAndEconomicCyclesAffection,
    costFormation,
    expenses,
    costsInfluenceFactors,
    costEstimation,
    logisticsAffect,
    fixedCostsDistributing,
    researchMainComponents,
    highestPriorityProjects,
    mainCostComponents,
    generalStructure,
    otherOperatingExpenses,
    controlStrategies,
    fixedExpensesTypes,
    financialExpensesAmount,
    nonOperatingExpenses,
    typesOfTaxes,
  } = businessPlanChapters.FINANCIAL_PLAN.keyAssumptions;

  const {
    netCashFlowsOperatingChange,
    cashFlowsInvestingStructure,
    netCashFlowInvestingChange,
    cashFlowsFinancingStructure,
    netCashFlowsFinancingChange,
  } = businessPlanChapters.FINANCIAL_PLAN.projectedCashFlow;

  const {
    companyMainComponents,
    companyEquityStructure,
    nonCurrentAssetsChange,
    shortTermFinancingSources,
    shortTermLiabilitiesFinance,
    structureOfNonCurrentAssets,
    longTermLiabilitiesMainComponents,
    shortTermLiabilitiesMainComponents,
  } = businessPlanChapters.FINANCIAL_PLAN.projectedBalanceSheets;

  const {
    solvency: { anticipatedOutcome: solvencyRatiosText },
    liquidity: { anticipatedOutcome: liquidityRatiosText },
    efficiency: { anticipatedOutcome: efficiencyRatiosText },
    investment: { investmentForecastedResult },
    profitability: { anticipatedOutcome: profitabilityRatiosText },
  } = businessPlanChapters.FINANCIAL_PLAN.financialPerformance;
  const salesAssumptions = [
    getMagazineTextObject(
      isExamplePage ? 'Revenue Projections. ' : t('pdfPages.mappings.financialPlan.revenueProj'),
      expectedRevenueVolume,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Sales Growth Factors. ' : t('pdfPages.mappings.financialPlan.salesGrowthFactors'),
      increaseFactors,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Price Changes. ' : t('pdfPages.mappings.financialPlan.priceChanges'),
      changeInPrices,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Seasonality Impact. ' : t('pdfPages.mappings.financialPlan.seasonImpact'),
      seasonalityAndEconomicCyclesAffection,
    ),
  ];
  const expensesAssumptions = [
    getMagazineTextObject(
      isExamplePage ? 'Cost Formation. ' : t('pdfPages.mappings.financialPlan.costFormation'),
      costFormation,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Variable vs. Fixed. ' : t('pdfPages.mappings.financialPlan.variableFixed'),
      expenses,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Variable Cost Factors. ' : t('pdfPages.mappings.financialPlan.variableCostFactors'),
      costsInfluenceFactors,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Labor and Materials. ' : t('pdfPages.mappings.financialPlan.labormaterials'),
      costEstimation,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Logistics Impact. ' : t('pdfPages.mappings.financialPlan.logisticImpact'),
      logisticsAffect,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Fixed Cost Distribution. ' : t('pdfPages.mappings.financialPlan.fixedCostDistribution'),
      fixedCostsDistributing,
    ),
    getMagazineTextObject(
      isExamplePage ? 'R&D Costs. ' : t('pdfPages.mappings.financialPlan.rdCosts'),
      researchMainComponents,
    ),
    getMagazineTextObject(
      isExamplePage ? 'R&D Priorities. ' : t('pdfPages.mappings.financialPlan.rdPriorities'),
      highestPriorityProjects,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Marketing Costs. ' : t('pdfPages.mappings.financialPlan.marketCosts'),
      mainCostComponents,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Administrative Expenses. ' : t('pdfPages.mappings.financialPlan.administrExpences'),
      generalStructure,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Other Operating Costs. ' : t('pdfPages.mappings.financialPlan.otherOperCosts'),
      otherOperatingExpenses,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Expense Control Strategies. ' : t('pdfPages.mappings.financialPlan.expenseControlStrategy'),
      controlStrategies,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Fixed Expenses Increase. ' : t('pdfPages.mappings.financialPlan.fixedExpensesIncrease'),
      fixedExpensesTypes,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Financial Expenses. ' : t('pdfPages.mappings.financialPlan.financialExpenses'),
      financialExpensesAmount,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Non-Operating Expenses. ' : t('pdfPages.mappings.financialPlan.nonOperatingExpenses'),
      nonOperatingExpenses,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Taxes and Fees. ' : t('pdfPages.mappings.financialPlan.taxesAndFees'),
      typesOfTaxes,
    ),
  ];
  const projectedCashFlow = [
    getMagazineTextObject(
      isExamplePage ? 'Operating Activities Share. ' : t('pdfPages.mappings.financialPlan.operActivitiesShare'),
      netCashFlowsOperatingChange,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Investing Activities Structure. ' : t('pdfPages.mappings.financialPlan.investActivStructure'),
      cashFlowsInvestingStructure,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Investing Activities Net Flow. ' : t('pdfPages.mappings.financialPlan.investActivNetFlow'),
      netCashFlowInvestingChange,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Financing Activities Structure. ' : t('pdfPages.mappings.financialPlan.finActivStructure'),
      cashFlowsFinancingStructure,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Financing Activities Share. ' : t('pdfPages.mappings.financialPlan.finActivShare'),
      netCashFlowsFinancingChange,
    ),
  ];
  const projectedBalanceSheet = [
    getMagazineTextObject(
      isExamplePage ? 'Current Assets Composition. ' : t('pdfPages.mappings.financialPlan.curAssetsCompos'),
      companyMainComponents,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Financing Current Assets. ' : t('pdfPages.mappings.financialPlan.finCurAssets'),
      shortTermLiabilitiesMainComponents,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Non-Current Assets Value. ' : t('pdfPages.mappings.financialPlan.nonCurAssetsVal'),
      nonCurrentAssetsChange,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Non-Current Assets Structure. ' : t('pdfPages.mappings.financialPlan.nonCurAssetsStructure'),
      structureOfNonCurrentAssets,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Current Liabilities Components. ' : t('pdfPages.mappings.financialPlan.curLiabilCompon'),
      shortTermLiabilitiesFinance,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Current Financing Sources.  ' : t('pdfPages.mappings.financialPlan.curFinSourse'),
      shortTermFinancingSources,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Non-Current Liabilities Components. ' : t('pdfPages.mappings.financialPlan.nonCurLiabCompon'),
      longTermLiabilitiesMainComponents,
    ),
    getMagazineTextObject(
      isExamplePage ? 'Equity Structure.  ' : t('pdfPages.mappings.financialPlan.equityStruc'),
      companyEquityStructure,
    ),
  ];

  if (businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS) {
    const financialPerformance = [
      [
        getMagazineTextObject(
          isExamplePage ? 'Investment Performance Ratio. ' : t('pdfPages.mappings.financialPlan.investPerfomanceRatio'),
          investmentForecastedResult,
        ),
      ],
      [
        getMagazineTextObject(
          isExamplePage ? 'Profitability Ratios. ' : t('pdfPages.mappings.financialPlan.profitRatios'),
          profitabilityRatiosText,
        ),
      ],
    ];

    return [salesAssumptions, expensesAssumptions, projectedCashFlow, ...financialPerformance];
  }

  const financialPerformance = [
    [getMagazineTextObject('', investmentForecastedResult)],
    [
      getMagazineTextObject(
        isExamplePage ? 'Profitability Ratios. ' : t('pdfPages.mappings.financialPlan.profitRatios'),
        profitabilityRatiosText,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Liquidity Ratios. ' : t('pdfPages.mappings.financialPlan.liquidRatios'),
        liquidityRatiosText,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Efficiency Ratios. ' : t('pdfPages.mappings.financialPlan.efficiencyRatios'),
        efficiencyRatiosText,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Solvency Ratios. ' : t('pdfPages.mappings.financialPlan.solvencyRatios'),
        solvencyRatiosText,
      ),
    ],
  ];

  return [salesAssumptions, expensesAssumptions, projectedCashFlow, projectedBalanceSheet, ...financialPerformance];
};

export const getPersonnelExpensesData = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
): IFinBlockTableProps => {
  const { total, personnelExpensesTable, personnelExpensesHeader, personnelExpensesVariants } =
    businessPlanChapters.FINANCIAL_PLAN.plannedExpenses.personnelExpenses;

  const { unit, currency } = businessPlanChapters.GLOBAL;
  const currencySymbol = getCurrencySymbol(currency);

  const personnelExpensesTableWithoutIds = personnelExpensesTable.map((item) => omit(item, 'id'));

  const isOnlyPercentsInTable = !total.column0 && !total.column1 && !total.column2;

  return {
    headers: personnelExpensesHeader
      ? Object.values(personnelExpensesHeader).map((item) =>
          isExamplePage ? (item?.toString() ?? '') : getTranslatedInitialValue(item?.toString() ?? ''),
        )
      : personnelExpensesColumns.map((item) =>
          isExamplePage ? (item?.toString() ?? '') : getTranslatedInitialValue(item),
        ),
    rows: [
      ...personnelExpensesVariants.map((heading, index) => ({
        type: 'regularText' as RowType,
        heading: isExamplePage ? heading : getTranslatedInitialValue(heading),
        values: isOnlyPercentsInTable
          ? []
          : Object.values(personnelExpensesTableWithoutIds[index === 0 ? 1 : index === 1 ? 0 : index]).map(
              convertToNumber,
            ),
      })),
      {
        type: 'boldBackgroundGray' as RowType,
        heading: isExamplePage ? 'Total' : t('total'),
        values: Object.values(total).map(convertToNumber),
      },
    ].filter((row) => row.values.slice(0, 3).some(Boolean)),
    unit: `${currencySymbol} ${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
  };
};

const getDonutChartProps = (
  isExamplePage: boolean,
  totalTable: IRow,
  table: IRow[],
  title: string,
  currencySymbol: string,
  colors?: string[],
  textColor?: '#fff' | '#000',
): IDonutWithText => ({
  title,
  colors,
  textColor,
  total: totalTable.column0 ? `${currencySymbol}${formatNumberToShort(Number(totalTable.column0))}` : '',
  ...table.reduce<{ labels: string[]; percentages: number[]; physicals: number[] }>(
    (result, { column0, column1, rowItemName }) => {
      const phisicalValue = formatNumber(Number(column0));
      if (phisicalValue) {
        result.labels.push(
          `${isExamplePage ? (rowItemName as string) : getTranslatedInitialValue(rowItemName as string)} - ${currencySymbol}${phisicalValue}`,
        );
        result.percentages.push(Number(column1));
        result.physicals.push(Number(column0));
      }

      return result;
    },
    {
      labels: [],
      percentages: [],
      physicals: [],
    },
  ),
});

export const getDonutChartsArray = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean) => {
  const { costOfSalesTable, totalCostOfSalesTable } = businessPlanChapters.FINANCIAL_PLAN.plannedExpenses.costOfSales;
  const { generalAndAdministrativeTable, totalGeneralAndAdministrativeTable } =
    businessPlanChapters.FINANCIAL_PLAN.plannedExpenses.generalAndAdministrative;
  const { salesAndMarketingTable, totalSalesAndMarketingTable } =
    businessPlanChapters.FINANCIAL_PLAN.plannedExpenses.salesAndMarketing;
  const { rAndDTable, totalRAndDTable } = businessPlanChapters.FINANCIAL_PLAN.plannedExpenses['r&D'];
  const { currency } = businessPlanChapters.GLOBAL;
  const currencySymbol = getCurrencySymbol(currency);

  const textColor = getColorType() === 'blueYellow' ? '#000' : '#fff';

  const fixedExpenses = getDonutChartProps(
    isExamplePage,
    totalCostOfSalesTable,
    costOfSalesTable,
    isExamplePage
      ? 'The Initial Monthly Cost of Sales of Products/Services (Fixed Expenses)'
      : t('pdfPages.mappings.financialPlan.donuts.initialMonthlyCost'),

    currencySymbol,
    getColorStylesByRecord(colorIndexesFixedExpenses),
    textColor,
  );
  const generalExpenses = getDonutChartProps(
    isExamplePage,
    totalGeneralAndAdministrativeTable,
    generalAndAdministrativeTable,
    isExamplePage
      ? 'The Initial Monthly General and Administrative Expenses'
      : t('pdfPages.mappings.financialPlan.donuts.initialMonthlyGeneral'),

    currencySymbol,
    getColorStylesByRecord(colorIndexesGeneralExpenses),
    textColor,
  );
  const marketingExpenses = getDonutChartProps(
    isExamplePage,
    totalSalesAndMarketingTable,
    salesAndMarketingTable,
    isExamplePage
      ? 'The Initial Monthly Sales and Marketing Expenses '
      : t('pdfPages.mappings.financialPlan.donuts.initialMonthlySales'),

    currencySymbol,
    getColorStylesByRecord(colorIndexesMarketingExpenses),
    textColor,
  );
  const rAndD = getDonutChartProps(
    isExamplePage,
    totalRAndDTable,
    rAndDTable,
    isExamplePage
      ? 'The Initial Monthly Research and Development (R&D) Expenses'
      : t('pdfPages.mappings.financialPlan.donuts.initialMonthlyResearch'),

    currencySymbol,
    getColorStylesByRecord(colorIndexesRAndD),
    textColor,
  );

  return [fixedExpenses, generalExpenses, marketingExpenses, rAndD];
};

export const getPlannedExpenses = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean) => {
  const { unit, period, periodTableHeadings, currency } = businessPlanChapters.GLOBAL;
  const { expensesVariants, expensesTable, expensesTotalValues } =
    businessPlanChapters.FINANCIAL_PLAN.plannedExpenses.totalExpenses;

  const colors = getColorStylesByRecord(colorIndexesPlannedExpenses);

  const headers = Object.values(periodTableHeadings[period]);
  const totals = Object.values(expensesTotalValues[period]).map(convertToNumber);
  const currencySymbol = getCurrencySymbol(currency);

  const { rows, datasets } = expensesVariants.reduce<{
    rows: IFinBlockTableRowItem[];
    datasets: IChartDatasetItem[];
  }>(
    (result, variant, index) => {
      const values = getPeriodValues(expensesTable[index], period);
      if (values.some(Boolean)) {
        result.rows.push({
          type: 'regularText',
          heading: isExamplePage ? variant : getTranslatedInitialValue(variant),
          values,
        });
        result.datasets.push({
          label: isExamplePage ? variant : getTranslatedInitialValue(variant),
          data: values,
        });
      }

      return result;
    },
    { rows: [], datasets: [] },
  );

  rows.push({
    type: 'boldBackgroundGray',
    heading: isExamplePage ? 'Total' : t('total'),
    values: totals,
  });

  return {
    plannedExpensesTable: {
      unit: `${currencySymbol} ${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
      headers,
      rows,
    },
    plannedExpensesChart: {
      unit: `${currencySymbol} ${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
      labels: headers,
      datasets,
      totals,
      colors,
    },
  };
};

export const getVariableExpenses = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean) => {
  const { products = [], currency } = businessPlanChapters.GLOBAL;
  const { costOfEachUnitTable = [], totalCostOfEachUnitTable } =
    businessPlanChapters.FINANCIAL_PLAN.plannedExpenses.costOfEachUnit;
  const currencySymbol = getCurrencySymbol(currency);

  const initialRowItems = {
    column0: '',
    column1: '',
    column2: '',
    column3: '',
    column4: '',
  };

  const sortedKeysOfTotalCostOfEachUnitTable = Object.keys({ ...initialRowItems, ...totalCostOfEachUnitTable })
    .sort()
    .filter((item) => item !== 'rowItemName');

  const sortedTotalValues: string[] = [];
  for (const key of sortedKeysOfTotalCostOfEachUnitTable) {
    if (totalCostOfEachUnitTable) {
      sortedTotalValues.push(totalCostOfEachUnitTable[key] as string);
    }
  }

  const totals = sortedTotalValues.map(convertToNumber).filter(Boolean);
  const unit = `${currencySymbol}`;

  const variableExpensesColors = getColorStylesByRecord(colorIndexesVariableExpenses);
  const costPerUnitColors = getColorStylesByRecord(colorIndexescostPerUnitChart);

  const { headers, costPerUnitData, productsIndexesToRender } = products.reduce<{
    headers: string[];
    costPerUnitData: string[];
    productsIndexesToRender: number[];
  }>(
    (result, { name = '', costPerUnit = '' }, index) => {
      if (costPerUnit) {
        result.headers.push(name);
        result.costPerUnitData.push(costPerUnit);
        result.productsIndexesToRender.push(index);
      }
      return result;
    },
    { headers: [], costPerUnitData: [], productsIndexesToRender: [] },
  );

  const { rows, datasets } = costOfEachUnitTable.reduce<{
    rows: IFinBlockTableRowItem[];
    datasets: IChartDatasetItem[];
  }>(
    (result, { rowItemName, ...items }) => {
      const values = Object.values({ ...initialRowItems, ...items })
        .map(convertToNumber)
        .filter((_, ind) => productsIndexesToRender.includes(ind));
      if (values.some(Boolean)) {
        result.rows.push({
          type: 'regularText',
          heading: isExamplePage ? String(rowItemName) : getTranslatedInitialValue(String(rowItemName)),
          values,
        });
        result.datasets.push({
          label: isExamplePage ? String(rowItemName) : getTranslatedInitialValue(String(rowItemName)),
          data: values,
        });
      }

      return result;
    },
    { rows: [], datasets: [] },
  );

  rows.push({
    type: 'boldBackgroundGray',
    heading: isExamplePage ? 'Total' : t('total'),
    values: totals,
  });

  const costPerUnitDatasets = [{ label: '', data: costPerUnitData }];

  return {
    variableExpensesTable: { unit, headers, rows },
    variableExpensesChart: {
      unit,
      labels: headers,
      datasets,
      totals,
      colors: variableExpensesColors,
    },
    costPerUnitChart: {
      unit,
      labels: headers,
      datasets: costPerUnitDatasets,
      totals: costPerUnitData.map(getNumberFromString),
      colors: costPerUnitColors,
    },
  };
};

export const getArrayOfSourcesOfProjectFinancingAndLoanRepaymentSchedule = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
) => {
  const { unit, period, periodTableHeadings, currency } = businessPlanChapters.GLOBAL;
  const { calendarPlan, loanRepaymentSchedule } = businessPlanChapters.FINANCIAL_PLAN.cashFlowElements;
  const currencySymbol = getCurrencySymbol(currency);

  const colorsProjectFinancing = getColorStylesByRecord(colorIndexesProjectFinancing);
  const colorsLoanRepaymentSchedule = getColorStylesByRecord(colorIndexesLoanRepaymentSchedule);

  const formatUnit = `${currencySymbol} ${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`;
  const labels = Object.values(periodTableHeadings[period]);

  return [
    {
      title: isExamplePage ? 'Sources of Project Financing' : t('pdfPages.mappings.financialPlan.sourcesOfProjectFin'),
      unit: formatUnit,
      labels,
      datasets: getChartDatasetItems(calendarPlan.variants, calendarPlan.table, period, isExamplePage),
      totals: getPeriodValues(calendarPlan.total, period),
      colors: colorsProjectFinancing,
    },
    {
      title: isExamplePage
        ? 'The Loan Repayment Schedule and Interest Payments on the Loan'
        : t('pdfPages.mappings.financialPlan.loanRepaymentSchedule'),
      unit: formatUnit,
      labels,
      datasets: getChartDatasetItems(
        loanRepaymentSchedule.variants,
        loanRepaymentSchedule.table,
        period,
        isExamplePage,
      ),
      totals: [],
      colors: colorsLoanRepaymentSchedule,
    },
  ].filter(({ datasets }) => datasets) as IChartWithStackedBars[];
};

export const getRatiosCharts = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean) => {
  const { unit, period, periodTableHeadings, currency } = businessPlanChapters.GLOBAL;
  const {
    profitability: { profitabilityVariants, profitabilityTable, profitabilityTotal },
    liquidity: { liquidityVariants, liquidityTable },
    efficiency: { efficiencyVariants, efficiencyTable },
    solvency: { solvencyVariants, solvencyTable },
  } = businessPlanChapters.FINANCIAL_PLAN.financialPerformance;
  const currencySymbol = getCurrencySymbol(currency);

  const type = getColorType();

  const formatUnit = `${currencySymbol} ${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`;
  const labels = Object.values(periodTableHeadings[period]).map((item) => item.toString());

  const solvencyRatiosData = solvencyTable.map((item) => getPeriodValues(item, period));
  const solvencyDatasetLabels: string[] = [];

  solvencyRatiosData.forEach((item, index) => {
    if (item.some(Boolean)) {
      solvencyDatasetLabels.push(
        isExamplePage ? solvencyVariants[index] : getTranslatedInitialValue(solvencyVariants[index]),
      );
    }
  });

  const profitabilityRatiosData = [
    getPeriodValues(profitabilityTable[2], period),
    getPeriodValues(profitabilityTotal, period),
  ];

  const openingBalanceSheetData = businessPlanChapters.FINANCIAL_PLAN.openingBalanceSheets;
  const shouldRenderRestLineCharts = shouldRenderFinancialData(openingBalanceSheetData);

  const lineCharts: ILinesChart[] = [
    {
      title: '',
      labels,
      datasets: getChartDatasetItems(profitabilityVariants.slice(0, 2), profitabilityTable, period, isExamplePage),
      colors: getColorStyles(colorsIndexesLineChart[type]),
      valueFormatter: (value) => `${value}%`,
    },
  ];

  const restLineCharts: ILinesChart[] = [
    {
      title: isExamplePage ? 'Liquidity Ratios' : t('pdfPages.mappings.financialPlan.lineCharts.liquidRatios'),
      labels,
      datasets: getChartDatasetItems(liquidityVariants, liquidityTable, period, isExamplePage),
      colors: getColorStyles(colorsIndexesLiquidity[type]),
      withDot: false,
    },
    {
      title: isExamplePage ? 'Efficiency Ratios' : t('pdfPages.mappings.financialPlan.lineCharts.efficiencyRatios'),
      labels,
      datasets: getChartDatasetItems(efficiencyVariants, efficiencyTable, period, isExamplePage),
      colors: getColorStyles(colorsIndexesEfficiency[type]),
    },
  ];

  if (shouldRenderRestLineCharts) {
    lineCharts.push(...restLineCharts);
  }

  return {
    profitabilityRatios: {
      title: '',
      unit: formatUnit,
      labels,
      datasetsLabels: [
        profitabilityVariants[2],
        isExamplePage ? 'Net Profit' : t('financialPlan.financialPerformance.profitability.netProfit'),
      ],
      data: profitabilityRatiosData.filter((item) => item.some(Boolean)),
      colors: getColorStyles(colorsIndexesRatios[type]),
    },
    lineCharts: lineCharts.filter(({ datasets }) => datasets) as ILinesChart[],
    solvencyRatios: {
      title: '',
      labels,
      datasetsLabels: solvencyDatasetLabels,
      data: solvencyRatiosData.filter((item) => item.some(Boolean)),
      colors: getColorStyles(colorsIndexesSolvencyRatios[type]),
    },
  };
};

export const getProjectedBalanceSheet = (
  businessPlanChapters: IBusinessToolForm,
  currentAssetsVariantsFromHook: string[],
  nonCurrentAssetsVariantsFromHook: string[],
  currentLiabilitiesVariantsFromHook: string[],
  nonCurrentLiabilitiesVariantsFromHook: string[],
  equityVariantsFromHook: string[],
  isExamplePage: boolean,
): IFinBlockTableProps => {
  const {
    currentAssetsTable,
    currentAssetsTableTotal,
    nonCurrentAssetsTable,
    nonCurrentAssetsTableTotal,
    totalAssets,
  } = businessPlanChapters.FINANCIAL_PLAN.projectedBalanceSheets.assets;

  const {
    currentLiabilitiesTable,
    nonCurrentLiabilitiesTable,
    equityTable,
    currentLiabilitiesTableTotal,
    nonCurrentLiabilitiesTableTotal,
    equityTableTotal,
    totalLiabilitiesAndEquity,
  } = businessPlanChapters.FINANCIAL_PLAN.projectedBalanceSheets.liabilitiesAndEquity;

  const { unit, period, periodTableHeadings, currency } = businessPlanChapters.GLOBAL;
  const headers = Object.values(periodTableHeadings[period]);
  const currencySymbol = getCurrencySymbol(currency);

  // CURRENT ASSETS
  const currentAssetsKeys = Object.values(CURRENT_ASSETS_TABLE_TOTAL_NAMES);
  const currentAssets = currentAssetsVariantsFromHook.reduce<{
    rows: IFinBlockTableRowItem[];
  }>(
    (result, variant, index) => {
      const key = currentAssetsKeys[index];
      const values = Object.values(currentAssetsTable?.[key]?.[period] || {}).map(convertToNumber);
      if (values.every((item) => !item)) {
        return result;
      }
      result.rows.push({
        type: 'regularText',
        heading: variant,
        values,
      });

      return result;
    },
    { rows: [] },
  );

  // NON CURRENT ASSETS
  const nonCurrentAssetsKeys = Object.values(NON_CURRENT_ASSETS_TABLE_TOTAL_NAMES);
  const nonCurrentAssets = nonCurrentAssetsVariantsFromHook.reduce<{
    rows: IFinBlockTableRowItem[];
  }>(
    (result, variant, index) => {
      const key = nonCurrentAssetsKeys[index];
      const values = Object.values(nonCurrentAssetsTable?.[key]?.[period] || {}).map(convertToNumber);
      if (values.every((item) => !item)) {
        return result;
      }
      result.rows.push({
        type: 'regularText',
        heading: variant,
        values,
      });

      return result;
    },
    { rows: [] },
  );

  // CURRENT LIABILITIES
  const currentLiabilitiesKeys = Object.values(CURRENT_LIABILITIES_TABLE_NAMES);
  const currentLiabilities = currentLiabilitiesVariantsFromHook.reduce<{
    rows: IFinBlockTableRowItem[];
  }>(
    (result, variant, index) => {
      const key = currentLiabilitiesKeys[index];
      const values = Object.values(currentLiabilitiesTable?.[key]?.[period] || {}).map(convertToNumber);
      if (values.every((item) => !item)) {
        return result;
      }
      result.rows.push({
        type: 'regularText',
        heading: variant,
        values,
      });

      return result;
    },
    { rows: [] },
  );

  // NON CURRENT LIABILITIES
  const nonCurrentLiabilitiesKeys = Object.values(NON_CURRENT_LIABILITIES_TABLE_NAMES);
  const nonCurrentLiabilities = nonCurrentLiabilitiesVariantsFromHook.reduce<{
    rows: IFinBlockTableRowItem[];
  }>(
    (result, variant, index) => {
      const key = nonCurrentLiabilitiesKeys[index];
      const values = Object.values(nonCurrentLiabilitiesTable?.[key]?.[period] || {}).map(convertToNumber);
      if (values.every((item) => !item)) {
        return result;
      }
      result.rows.push({
        type: 'regularText',
        heading: variant,
        values,
      });

      return result;
    },
    { rows: [] },
  );

  // EQUITY
  const equityKeys = Object.values(EQUITY_TABLE_NAMES);
  const equity = equityVariantsFromHook.reduce<{
    rows: IFinBlockTableRowItem[];
  }>(
    (result, variant, index) => {
      const key = equityKeys[index];
      const values = Object.values(equityTable?.[key]?.[period] || {}).map(convertToNumber);
      if (values.every((item) => !item)) {
        return result;
      }
      result.rows.push({
        type: 'regularText',
        heading: variant,
        values,
      });

      return result;
    },
    { rows: [] },
  );

  const finalRows: IFinBlockTableRowItem[] = [
    {
      type: 'boldDarkGreen',
      heading: isExamplePage ? 'Assets' : t('financialPlan.projectedBalanceSheets.assets.title'),
    },
  ];

  const currenAssetsRows: IFinBlockTableRowItem[] = [
    {
      type: 'boldBlackText',
      heading: isExamplePage ? 'Current assets' : t('financialPlan.projectedBalanceSheets.assets.currentAssets.title'),
    },
    ...currentAssets.rows,
    {
      type: 'boldGreenText',
      heading: isExamplePage
        ? 'Total current assets'
        : t('financialPlan.projectedBalanceSheets.assets.currentAssets.totalRowTitle'),
      values: Object.values(currentAssetsTableTotal[period]).map(convertToNumber),
    },
  ];

  const nonCurrentAssetsRows: IFinBlockTableRowItem[] = [
    {
      type: 'boldBlackText',
      heading: isExamplePage
        ? 'Non-current assets'
        : t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.title'),
    },
    ...nonCurrentAssets.rows,
    {
      type: 'boldGreenText',
      heading: isExamplePage
        ? 'Total non-current assets'
        : t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.totalRowTitle'),
      values: Object.values(nonCurrentAssetsTableTotal[period]).map(convertToNumber),
    },
  ];
  const currentLiabilitiesRows: IFinBlockTableRowItem[] = [
    {
      type: 'boldBlackText',
      heading: isExamplePage
        ? 'Current liabilities'
        : t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.title'),
    },
    ...currentLiabilities.rows,
    {
      type: 'boldGreenText',
      heading: isExamplePage
        ? 'Total current liabilities'
        : t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.totalRowTitle'),
      values: Object.values(currentLiabilitiesTableTotal[period]).map(convertToNumber),
    },
  ];
  const nonCurrentLiabilitiesRows: IFinBlockTableRowItem[] = [
    {
      type: 'boldBlackText',
      heading: isExamplePage
        ? 'Non-current liabilities'
        : t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.nonCurrentLiabilities.title'),
    },
    ...nonCurrentLiabilities.rows,
    {
      type: 'boldGreenText',
      heading: isExamplePage
        ? 'Total non-current liabilities'
        : t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.nonCurrentLiabilities.totalRowTitle'),
      values: Object.values(nonCurrentLiabilitiesTableTotal[period]).map(convertToNumber),
    },
  ];

  const equityRows: IFinBlockTableRowItem[] = [
    {
      type: 'boldBlackText',
      heading: isExamplePage ? 'Equity' : t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.equity.title'),
    },
    ...equity.rows,
    {
      type: 'boldGreenText',
      heading: isExamplePage
        ? 'Total equity'
        : t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.equity.totalRowTitle'),
      values: Object.values(equityTableTotal[period]).map(convertToNumber),
    },
  ];

  if (currentAssets.rows.length > 0) {
    finalRows.push(...currenAssetsRows);
  }
  if (nonCurrentAssets.rows.length > 0) {
    finalRows.push(...nonCurrentAssetsRows);
  }
  if (currentAssets.rows.length > 0 || nonCurrentAssets.rows.length > 0) {
    finalRows.push({
      type: 'boldGreenText',
      heading: isExamplePage ? 'Total assets' : t('financialPlan.projectedBalanceSheets.assets.totalAssets'),
      values: Object.values(totalAssets[period]).map(convertToNumber),
    });
  }
  if (currentLiabilities.rows.length > 0 || nonCurrentLiabilities.rows.length > 0 || equity.rows.length > 0) {
    finalRows.push({
      type: 'boldDarkGreen',
      heading: isExamplePage
        ? 'Equity and liabilities'
        : t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.title'),
    });
  }

  if (currentLiabilities.rows.length > 0) {
    finalRows.push(...currentLiabilitiesRows);
  }
  if (nonCurrentLiabilities.rows.length > 0) {
    finalRows.push(...nonCurrentLiabilitiesRows);
  }
  if (equity.rows.length > 0) {
    finalRows.push(...equityRows);
  }
  if (currentLiabilities.rows.length > 0 || nonCurrentLiabilities.rows.length > 0 || equity.rows.length > 0) {
    finalRows.push({
      type: 'boldGreenText',
      heading: isExamplePage
        ? 'Total liabilities and equity'
        : t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.totalLiabilitiesAndEquity'),
      values: Object.values(totalLiabilitiesAndEquity[period]).map(convertToNumber),
    });
  }

  return {
    headers,
    rows: finalRows,
    unit: `${currencySymbol} ${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
  };
};

export const getProjectedBalanceSheetChartsData = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean) => {
  const { currentAssetsTableTotal, nonCurrentAssetsTableTotal } =
    businessPlanChapters.FINANCIAL_PLAN.projectedBalanceSheets.assets;

  const { currentLiabilitiesTableTotal, nonCurrentLiabilitiesTableTotal, equityTableTotal } =
    businessPlanChapters.FINANCIAL_PLAN.projectedBalanceSheets.liabilitiesAndEquity;

  const { unit, period, periodTableHeadings, currency } = businessPlanChapters.GLOBAL;
  const labels = Object.values(periodTableHeadings[period]);

  const colors = getColorStylesByRecord(colorIndexesProjectedBalance);
  const currencySymbol = getCurrencySymbol(currency);

  const assetsDatasetsLabels: string[] = [];
  const assetsData: (string | number)[][] = [];

  if (Object.values(currentAssetsTableTotal[period]).map(convertToNumber).some(Boolean)) {
    assetsDatasetsLabels.push(
      isExamplePage ? 'Current assets' : t('financialPlan.projectedBalanceSheets.assets.currentAssets.title'),
    );
    assetsData.push(Object.values(currentAssetsTableTotal[period]).map(convertToNumber));
  }
  if (Object.values(nonCurrentAssetsTableTotal[period]).map(convertToNumber).some(Boolean)) {
    assetsDatasetsLabels.push(
      isExamplePage ? 'Non-current assets' : t('financialPlan.projectedBalanceSheets.assets.nonCurrentAssets.title'),
    );
    assetsData.push(Object.values(nonCurrentAssetsTableTotal[period]).map(convertToNumber));
  }

  const assetsChartData: IChartWithBars = {
    title: '',
    colors: colors.slice(0, 2),
    source: '',
    unit: `${currencySymbol} ${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
    datasetsLabels: assetsDatasetsLabels,
    labels,
    data: assetsData,
  };

  const liabilitiesAndEquityDatasetsLabels: string[] = [];
  const liabilitiesAndEquityData: (string | number)[][] = [];

  if (Object.values(currentLiabilitiesTableTotal[period]).map(convertToNumber).some(Boolean)) {
    liabilitiesAndEquityDatasetsLabels.push(
      isExamplePage
        ? 'Current liabilities'
        : t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.title'),
    );
    liabilitiesAndEquityData.push(Object.values(currentLiabilitiesTableTotal[period]).map(convertToNumber));
  }
  if (Object.values(nonCurrentLiabilitiesTableTotal[period]).map(convertToNumber).some(Boolean)) {
    liabilitiesAndEquityDatasetsLabels.push(
      isExamplePage
        ? 'Non-current liabilities'
        : t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.nonCurrentLiabilities.title'),
    );
    liabilitiesAndEquityData.push(Object.values(nonCurrentLiabilitiesTableTotal[period]).map(convertToNumber));
  }
  if (Object.values(equityTableTotal[period]).map(convertToNumber).some(Boolean)) {
    liabilitiesAndEquityDatasetsLabels.push(
      isExamplePage ? 'Equity' : t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.equity.title'),
    );
    liabilitiesAndEquityData.push(Object.values(equityTableTotal[period]).map(convertToNumber));
  }

  const equityAndLiabilitiesChartData: IChartWithBars = {
    title: '',
    colors: colors,
    source: '',
    unit: `${currencySymbol} ${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
    datasetsLabels: liabilitiesAndEquityDatasetsLabels,
    labels,
    data: liabilitiesAndEquityData,
  };

  return { assetsChartData, equityAndLiabilitiesChartData };
};

const getFormattedTableNumberValues = (periodValues?: Record<string, string | number>) => {
  return periodValues ? Object.values(periodValues).map((value) => convertToNumber(value)) : [];
};

const getIndirectOperatingActivitiesTableRows = (
  businessPlanChapters: IBusinessToolForm,
  adjustmentsForLinkedFieldsValue: typeof adjustmentsForLinkedFields,
  operatingAssetsAndLiabilitiesLinkedFieldsValue: typeof operatingAssetsAndLiabilitiesLinkedFields,
  isExamplePage: boolean,
): IFinBlockTableRowItem[] => {
  const { period } = businessPlanChapters.GLOBAL;
  const {
    adjustmentsFor,
    operatingAssetsAndLiabilitiesChanges,
    netCashProvidedOperatingActivities,
    netIncomeProfitForPeriod,
  } = businessPlanChapters.FINANCIAL_PLAN.projectedCashFlow.inDirect;

  const adjustmentsData: IFinBlockTableRowItem[] = (
    Object.keys(adjustmentsForLinkedFieldsValue) as Array<keyof typeof adjustmentsForLinkedFields>
  ).map((key) => ({
    type: 'regularText',
    heading: adjustmentsForLinkedFieldsValue[key].title,
    values: getFormattedTableNumberValues(adjustmentsFor[key][period]),
  }));

  const operatingAssetsAndLiabilitiesChangesData: IFinBlockTableRowItem[] = (
    Object.keys(operatingAssetsAndLiabilitiesLinkedFieldsValue) as Array<
      keyof typeof operatingAssetsAndLiabilitiesLinkedFields
    >
  ).map((key) => ({
    type: 'regularText',
    heading: operatingAssetsAndLiabilitiesLinkedFieldsValue[key].title,
    values: getFormattedTableNumberValues(operatingAssetsAndLiabilitiesChanges[key][period]),
  }));

  const netIncomeTotalValuesRow: IFinBlockTableRowItem = {
    type: 'boldGreenText',
    heading: isExamplePage
      ? 'Net income/profit for the period'
      : t('financialPlan.projectedCashFlow.inDirect.netIncomeProfitForPeriod'),
    values: getFormattedTableNumberValues(netIncomeProfitForPeriod[period]),
  };
  const totalValuesRow: IFinBlockTableRowItem = {
    type: 'boldGreenText',
    heading: isExamplePage
      ? 'Net cash provided by operating activities'
      : t('financialPlan.projectedCashFlow.inDirect.netCashProvidedOperatingActivities'),
    values: getFormattedTableNumberValues(netCashProvidedOperatingActivities[period]),
  };

  return [
    {
      type: 'boldDarkGreen',
      heading: isExamplePage
        ? 'Cash Flows from Operating Activities (Indirect)'
        : t('pdfPages.mappings.financialPlan.cashFlowsIndirect'),
    },
    netIncomeTotalValuesRow,
    {
      type: 'regularText',
      heading: isExamplePage ? 'Adjustments for:' : t('financialPlan.projectedCashFlow.inDirect.adjustmentsFor.title'),
    },
    ...adjustmentsData,
    {
      type: 'regularText',
      heading: isExamplePage
        ? 'Changes in operating assets and liabilities:'
        : t('financialPlan.projectedCashFlow.inDirect.operatingAssetsAndLiabilities.title'),
    },
    ...operatingAssetsAndLiabilitiesChangesData,
    totalValuesRow,
  ];
};

const getDirectOperatingActivitiesTableRows = (
  businessPlanChapters: IBusinessToolForm,
  operatingActivitiesRowsValues: typeof operatingActivitiesRows,
  isExamplePage: boolean,
): IFinBlockTableRowItem[] => {
  const { period } = businessPlanChapters.GLOBAL;
  const { cashFlowsOperatingActivities } = businessPlanChapters.FINANCIAL_PLAN.projectedCashFlow.direct;

  const directOperatingActivitiesHeadingRow: IFinBlockTableRowItem = {
    type: 'boldDarkGreen',
    heading: isExamplePage
      ? 'Cash flows from operating activities (direct)'
      : t('pdfPages.mappings.financialPlan.cashFlowsDirect'),
  };

  const directOperatingActivitiesData: IFinBlockTableRowItem[] = operatingActivitiesRowsValues.map(
    ({ name, title }) => ({
      type: 'regularText',
      heading: title,
      values: getFormattedTableNumberValues(cashFlowsOperatingActivities[name][period]),
    }),
  );

  const totalValuesRow: IFinBlockTableRowItem = {
    type: 'boldGreenText',
    heading: isExamplePage
      ? 'Net cash provided by operating activities'
      : t('financialPlan.projectedCashFlow.inDirect.netCashProvidedOperatingActivities'),
    values: getFormattedTableNumberValues(cashFlowsOperatingActivities.netCashOperatingActivities[period]),
  };

  return [directOperatingActivitiesHeadingRow, ...directOperatingActivitiesData, totalValuesRow];
};

const getInvestedActivitiesTableRows = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
): IFinBlockTableRowItem[] => {
  const { period } = businessPlanChapters.GLOBAL;
  const { cashFLowsInvestingActivities } = businessPlanChapters.FINANCIAL_PLAN.projectedCashFlow.direct;

  const {
    table,
    // TODO delete if client agreed
    // lossOnSaleOfAssets,
    total,
    purchasePropertyPlantEquipment,
  } = cashFLowsInvestingActivities;

  const investingActivitiesHeadingRow: IFinBlockTableRowItem = {
    type: 'boldDarkGreen',
    heading: isExamplePage
      ? 'Cash flows from investing activities'
      : t('pdfPages.mappings.financialPlan.cashFlowsFromInvest'),
  };

  const purchasePropertyPlantEquipmentRow: IFinBlockTableRowItem = {
    type: 'regularText',
    heading: isExamplePage
      ? 'Purchase of property, plant and equipment'
      : t('pdfPages.mappings.financialPlan.purchaseOfProperty'),
    values: getFormattedTableNumberValues(purchasePropertyPlantEquipment[period]),
  };

  // TODO delete if client agreed
  // const lossOnSaleOfAssetsRow: IFinBlockTableRowItem = {
  //   type: 'regularText',
  //   heading: 'Loss on sale of assets',
  //   values: getFormattedTableNumberValues(lossOnSaleOfAssets[period]),
  // };

  const investingActivitiesRowsTitles = getInvestingActivitiesRowsTitles(isExamplePage);

  const mainDataRows: IFinBlockTableRowItem[] = investingActivitiesRowsTitles.map((title, index) => {
    return {
      type: 'regularText',
      heading: title,
      values: getFormattedTableNumberValues(table[index][period]),
    };
  });

  const totalValuesRow: IFinBlockTableRowItem = {
    type: 'boldGreenText',
    heading: isExamplePage
      ? 'Net cash used in investing activities'
      : t('pdfPages.mappings.financialPlan.netCashUsedInInvesting'),
    values: getFormattedTableNumberValues(total[period]),
  };

  return [
    investingActivitiesHeadingRow,
    purchasePropertyPlantEquipmentRow,
    ...mainDataRows.slice(0, 2),
    // TODO delete if client agreed
    // lossOnSaleOfAssetsRow,
    ...mainDataRows.slice(2),
    totalValuesRow,
  ];
};

const getFinancingActivitiesTableRows = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
): IFinBlockTableRowItem[] => {
  const { period } = businessPlanChapters.GLOBAL;
  const {
    cashFLowsFinancingActivities: { table, total, proceedsIssuanceDebt, repaymentDebt, proceedsIssuanceEquity },
  } = businessPlanChapters.FINANCIAL_PLAN.projectedCashFlow.direct;

  const financingActivitiesHeadingRow: IFinBlockTableRowItem = {
    type: 'boldDarkGreen',
    heading: isExamplePage
      ? 'Cash flows from financing activities'
      : t('pdfPages.mappings.financialPlan.cashFlowsFromFinancing'),
  };

  const proceedsIssuanceDebtRow: IFinBlockTableRowItem = {
    type: 'regularText',
    heading: isExamplePage
      ? 'Proceeds from issuance of debt'
      : t('financialPlan.projectedCashFlow.direct.financingActivities.proceedsIssuanceDebt'),
    values: getFormattedTableNumberValues(proceedsIssuanceDebt[period]),
  };

  const repaymentDebtRow: IFinBlockTableRowItem = {
    type: 'regularText',
    heading: isExamplePage
      ? 'Repayment of debt'
      : t('financialPlan.projectedCashFlow.direct.financingActivities.repaymentDebt'),
    values: getFormattedTableNumberValues(repaymentDebt[period]),
  };

  const proceedsIssuanceEquityRow: IFinBlockTableRowItem = {
    type: 'regularText',
    heading: isExamplePage
      ? 'Proceeds from issuance of equity'
      : t('financialPlan.projectedCashFlow.direct.financingActivities.proceedsIssuanceEquity'),
    values: getFormattedTableNumberValues(proceedsIssuanceEquity[period]),
  };

  const financingActivitiesRowsTitles = getFinancingActivitiesRowsTitles(isExamplePage)

  const mainTableData: IFinBlockTableRowItem[] = financingActivitiesRowsTitles.map((title, index) => {
    return {
      type: 'regularText',
      heading: title,
      values: getFormattedTableNumberValues(table[index][period]),
    };
  });

  const totalValuesRow: IFinBlockTableRowItem = {
    type: 'boldGreenText',
    heading: isExamplePage
      ? 'Net cash (used in)/generated from financing activities'
      : t('financialPlan.projectedCashFlow.direct.financingActivities.netCashUsedGeneratedFinancingActivities'),
    values: getFormattedTableNumberValues(total[period]),
  };

  return [
    financingActivitiesHeadingRow,
    proceedsIssuanceDebtRow,
    repaymentDebtRow,
    proceedsIssuanceEquityRow,
    ...mainTableData,
    totalValuesRow,
  ];
};

export const getProjectedCashFlowTableData = (
  businessPlanChapters: IBusinessToolForm,
  adjustmentsForLinkedFieldsValue: typeof adjustmentsForLinkedFields,
  operatingAssetsAndLiabilitiesLinkedFieldsValue: typeof operatingAssetsAndLiabilitiesLinkedFields,
  operatingActivitiesRowsValues: typeof operatingActivitiesRows,
  isExamplePage: boolean,
): IFinBlockTableProps => {
  const { direct, selectedType } = businessPlanChapters.FINANCIAL_PLAN.projectedCashFlow;
  const { period, unit, currency, periodTableHeadings } = businessPlanChapters.GLOBAL;
  const headers = Object.values(periodTableHeadings[period]);
  const symbol = currency ? currency.symbol : '';

  const { netIncreaseDecreaseCashEquivalents, cashEquivalentsBeginningPeriod, cashEquivalentsEndPeriod } = direct;

  const investedActivitiesRows = getInvestedActivitiesTableRows(businessPlanChapters, isExamplePage);
  const intermediateOperatingActivitiesRows =
    selectedType === 'direct'
      ? getDirectOperatingActivitiesTableRows(businessPlanChapters, operatingActivitiesRowsValues, isExamplePage)
      : getIndirectOperatingActivitiesTableRows(
          businessPlanChapters,
          adjustmentsForLinkedFieldsValue,
          operatingAssetsAndLiabilitiesLinkedFieldsValue,
          isExamplePage,
        );
  const financingActivitiesRows = getFinancingActivitiesTableRows(businessPlanChapters, isExamplePage);

  const finalOperatingActivitiesRows = intermediateOperatingActivitiesRows
    .slice(1)
    .some((row) => row?.values?.some(Boolean))
    ? intermediateOperatingActivitiesRows
    : [];

  const preparedFinalOperatingActivitiesRows = finalOperatingActivitiesRows.reduce(
    (acc: IFinBlockTableRowItem[], current: IFinBlockTableRowItem) => {
      if (
        current.heading ===
          (isExamplePage
            ? 'Increase/Decrease in accounts receivable'
            : t(
                'financialPlan.projectedCashFlow.inDirect.operatingAssetsAndLiabilities.increaseDecreaseAccountsReceivable',
              )) ||
        current.heading ===
          (isExamplePage
            ? 'Increase/Decrease in inventories'
            : t(
                'financialPlan.projectedCashFlow.inDirect.operatingAssetsAndLiabilities.increaseDecreaseInventories',
              )) ||
        current.heading ===
          (isExamplePage
            ? 'Increase/Decrease in other operating assets and liabilities'
            : t(
                'financialPlan.projectedCashFlow.inDirect.operatingAssetsAndLiabilities.increaseDecreaseOtherOperatingAssetsLiabilities',
              ))
      ) {
        const convertedToNegativeValues = current?.values?.map((item) => +item * -1);
        return [...acc, { ...current, values: convertedToNegativeValues }];
      } else {
        return [...acc, current];
      }
    },
    [] as IFinBlockTableRowItem[],
  );

  const finalInvestedActivitiesRows = investedActivitiesRows.slice(1).some((row) => row?.values?.some(Boolean))
    ? investedActivitiesRows
    : [];
  const finalFinancingActivitiesRows = financingActivitiesRows.slice(1).some((row) => row?.values?.some(Boolean))
    ? financingActivitiesRows
    : [];

  const netIncreaseDecreaseCashEquivalentsRow: IFinBlockTableRowItem = {
    type: 'boldGreenText',
    heading: isExamplePage
      ? 'Net increase/(decrease) in cash and cash equivalents'
      : t('financialPlan.projectedCashFlow.direct.netIncreaseDecreaseCashEquivalents'),
    values: getFormattedTableNumberValues(netIncreaseDecreaseCashEquivalents?.[period]),
  };

  const cashEquivalentsBeginningPeriodRow: IFinBlockTableRowItem = {
    type: 'regularText',
    heading: isExamplePage
      ? 'Cash and cash equivalents, beginning of period'
      : t('financialPlan.projectedCashFlow.direct.cashEquivalentsBeginningPeriod'),
    values: getFormattedTableNumberValues(cashEquivalentsBeginningPeriod?.[period]),
  };

  const cashEquivalentsEndPeriodRow: IFinBlockTableRowItem = {
    type: 'regularText',
    heading: isExamplePage
      ? 'Cash and cash equivalents, end of period'
      : t('financialPlan.projectedCashFlow.direct.cashEquivalentsEndPeriod'),
    values: getFormattedTableNumberValues(cashEquivalentsEndPeriod?.[period]),
  };

  return {
    unit: `${symbol} ${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
    headers,
    rows: [
      ...preparedFinalOperatingActivitiesRows,
      ...finalInvestedActivitiesRows,
      ...finalFinancingActivitiesRows,
      netIncreaseDecreaseCashEquivalentsRow,
      cashEquivalentsBeginningPeriodRow,
      cashEquivalentsEndPeriodRow,
    ].filter((row) => (row.values ? row.values.some((value) => value !== 0) : true)),
  };
};

const orderedSubRowsNames = [
  FUNDING_DIRECTIONS_NAMES.R_AND_D_SUB_ROWS,
  FUNDING_DIRECTIONS_NAMES.MARKETING_AND_SALES_SUB_ROWS,
  FUNDING_DIRECTIONS_NAMES.WORKING_CAPITAL_SUB_ROW,
  FUNDING_DIRECTIONS_NAMES.CONSTRUCTION_SUB_ROW,
  FUNDING_DIRECTIONS_NAMES.EQUIPMENT_PURCHASE_SUB_ROW,
  FUNDING_DIRECTIONS_NAMES.OTHER_SUB_ROW,
];

interface IRowData {
  name: string;
  values: Record<string, string | number>;
  type: 'row' | 'subRow';
}

const concatRowsAndSubRowsData = (fundingDirections: IFundingDirectionsFormValuesType, period: IPeriodNames) => {
  const { variants, table } = fundingDirections;

  return variants.reduce((acc: IRowData[], variant: string, index: number) => {
    const rowHasOnlyZeroes = Object.values(table[index][period]).every((value) => Number(value) === 0);

    const currentSubRowsName = orderedSubRowsNames[index];

    const subRowsWithNonZeroValues = fundingDirections[currentSubRowsName].filter((item) =>
      Object.values(item[period]).some((value) => Number(value) !== 0),
    );

    if (rowHasOnlyZeroes) {
      return acc;
    }

    const currentSubRowsData: IRowData[] = subRowsWithNonZeroValues.map((item) => ({
      type: 'subRow',
      name: item.name,
      values: item[period],
    }));

    const currentRowData: IRowData = {
      type: 'row',
      name: variant,
      values: table[index][period],
    };

    return [...acc, currentRowData, ...currentSubRowsData];
  }, []);
};

export const getProjectedCashFlowChartData = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
): IMixedChart => {
  const { period, currency, unit, periodTableHeadings } = businessPlanChapters.GLOBAL;
  const {
    cashFlowsOperatingActivities: { netCashOperatingActivities },
    cashFLowsInvestingActivities: { total: netCashInvestingActivities },
    cashFLowsFinancingActivities: { total: netCashFinancingActivities },
    netIncreaseDecreaseCashEquivalents,
  } = businessPlanChapters.FINANCIAL_PLAN.projectedCashFlow.direct;
  const symbol = currency ? currency.symbol : '';

  const colors = getColorStylesByRecord(colorIndexesCashFlow);

  const headers = Object.values(periodTableHeadings[period]);

  const lineChartData: IMixedChartDatasetItem = {
    type: 'line',
    values: getFormattedTableNumberValues(netIncreaseDecreaseCashEquivalents[period]),
  };

  const barChartData: IMixedChartDatasetItem[] = [
    netCashOperatingActivities,
    netCashInvestingActivities,
    netCashFinancingActivities,
  ].map((item) => ({ type: 'bar', values: getFormattedTableNumberValues(item[period]) }));

  const finalDatasetsLabels: string[] = [];
  const labels = [
    isExamplePage
      ? 'Net cash provided by operating activities'
      : t('financialPlan.projectedCashFlow.inDirect.netCashProvidedOperatingActivities'),
    isExamplePage
      ? 'Net cash used in investing activities'
      : t('pdfPages.mappings.financialPlan.netCashUsedInInvesting'),
    isExamplePage
      ? 'Net cash (used in)/generated from financing activities'
      : t('financialPlan.projectedCashFlow.direct.financingActivities.netCashUsedGeneratedFinancingActivities'),
  ];
  barChartData.forEach((item, index) => {
    if (item.values.some(Boolean)) {
      finalDatasetsLabels.push(labels[index]);
    }
  });

  if (lineChartData.values.some(Boolean)) {
    finalDatasetsLabels.push(
      isExamplePage
        ? 'Net increase/(decrease) in cash and cash equivalents'
        : t('financialPlan.projectedCashFlow.direct.netIncreaseDecreaseCashEquivalents'),
    );
  }

  return {
    title: '',
    unit: `${symbol} ${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
    colors,
    labels: headers,
    datasetsLabels: finalDatasetsLabels,
    data: [...barChartData, lineChartData].filter((item) => item.values.some(Boolean)),
  };
};

export const getFundingDirectionsTableData = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
): IFinBlockTableProps => {
  const { fundingDirections } = businessPlanChapters.FINANCIAL_PLAN.cashFlowElements;
  const { unit, period, periodTableHeadings, currency } = businessPlanChapters.GLOBAL;
  const { total } = fundingDirections;
  const headers = Object.values(periodTableHeadings[period]);
  const rowsData = concatRowsAndSubRowsData(fundingDirections, period);
  const symbol = currency?.symbol || '';

  const mainRows: IFinBlockTableRowItem[] = rowsData.map(({ name, values, type }) => ({
    type: type === 'row' ? 'boldGreenText' : 'regularText',
    valuesType: 'regularText',
    heading: name,
    values: getFormattedTableNumberValues(values),
  }));

  const totalValuesRow: IFinBlockTableRowItem = {
    type: 'boldBackgroundGray',
    heading: isExamplePage ? 'Total' : t('total'),
    values: mainRows.length ? getFormattedTableNumberValues(total[period]) : [],
  };

  return {
    rows: mainRows.length ? [...mainRows, totalValuesRow] : mainRows,
    unit: `${symbol} ${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
    headers,
  };
};

export const getFundingDirectionsChartData = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
): IChartWithStackedBars => {
  const { fundingDirections } = businessPlanChapters.FINANCIAL_PLAN.cashFlowElements;
  const { unit, period, periodTableHeadings, currency } = businessPlanChapters.GLOBAL;
  const { total, variants, table } = fundingDirections;
  const headers = Object.values(periodTableHeadings[period]);
  const totalValues = getFormattedTableNumberValues(total[period]);
  const symbol = currency?.symbol || '';

  const colors = getColorStylesByRecord(colorIndexesFundingDirections);

  const datasets = variants.reduce<IChartDatasetItem[]>((acc, variant, index) => {
    const data = getFormattedTableNumberValues(table[index][period]);
    if (data.some(Boolean)) {
      acc.push({ label: isExamplePage ? variant : getTranslatedInitialValue(variant), data: data });
    }
    return acc;
  }, []);

  return {
    title: '',
    unit: `${symbol} ${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
    labels: headers,
    datasets: datasets,
    totals: totalValues,
    colors,
  };
};

const getRatiosValue = (value: number, isIRR: boolean = false) => {
  if (isIRR && isNaN(value)) {
    return '—';
  }
  return value === null || isNaN(value) ? '' : convertToNumber(value);
};
export const getInvestmentPerformanceRatios = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
): IFinBlockTableProps => {
  const { netPresentValue, paybackPeriod, rateOfReturn, returnOnAssets, returnOnEquity, returnOnInvestment } =
    businessPlanChapters.FINANCIAL_PLAN.financialPerformance.investment.investmentTable;

  const { currency, period, unit } = businessPlanChapters.GLOBAL;
  const symbol = currency?.symbol || '';

  const rows: IFinBlockTableRowItem[] = [
    {
      type: 'regularText',
      heading: isExamplePage
        ? 'ROI (Return on Investment), %'
        : t('pdfPages.mappings.financialPlan.perfomanceRatios.ROI'),
      values: ['>10', getRatiosValue(returnOnInvestment[getYearsPeriod(period)])],
    },
    {
      type: 'regularText',
      heading: isExamplePage ? 'ROE (Return on Equity), %' : t('pdfPages.mappings.financialPlan.perfomanceRatios.ROE'),
      values: ['>15', getRatiosValue(returnOnEquity[getYearsPeriod(period)])],
    },
    {
      type: 'regularText',
      heading: isExamplePage ? 'ROA (Return on Assets), %' : t('pdfPages.mappings.financialPlan.perfomanceRatios.ROA'),
      values: ['>5', getRatiosValue(returnOnAssets[getYearsPeriod(period)])],
    },
    {
      type: 'regularText',
      heading: isExamplePage
        ? 'IRR (Internal Rate of Return), %'
        : t('pdfPages.mappings.financialPlan.perfomanceRatios.IRR'),
      values: ['>10', getRatiosValue(rateOfReturn[getYearsPeriod(period)], true)],
    },
    {
      type: 'regularText',
      heading: `${isExamplePage ? 'NPV (Net Present Value)' : t('pdfPages.mappings.financialPlan.perfomanceRatios.NPV')}, ${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)} ${symbol}`,
      values: ['', formatNumber(Number(netPresentValue[getYearsPeriod(period)]), 2)],
    },
    {
      type: 'regularText',
      heading: isExamplePage
        ? 'Payback Period, months'
        : t('pdfPages.mappings.financialPlan.perfomanceRatios.payBackPeriod'),
      values: ['<84', paybackPeriod],
    },
  ];

  const finalRows: IFinBlockTableRowItem[] = [];
  rows.forEach((item) => {
    if (item.values?.slice(1).some(Boolean)) {
      finalRows.push(item);
    }
  });

  return {
    headers: [
      isExamplePage ? 'Ratios' : t('pdfPages.mappings.financialPlan.ratios'),
      isExamplePage ? 'Benchmark value' : t('pdfPages.mappings.financialPlan.benchMarkValue'),
      isExamplePage ? 'The company’s indicator' : t('pdfPages.mappings.financialPlan.companyIndicator'),
    ],
    rows: finalRows,
  };
};

export const getOtherPerformanceRatios = (
  businessPlanChapters: IBusinessToolForm,
  businessPlanType: IBusinessToolType,
  isExamplePage: boolean,
): IFinBlockTableProps => {
  const { period, periodTableHeadings } = businessPlanChapters.GLOBAL;
  const headers = Object.values(periodTableHeadings[period]);
  const monthsHeaders: string[] = Array.from({ length: 12 }).map((_, index) => `M${index + 1}`);

  const { profitabilityVariants, profitabilityTable, profitabilityTotal } =
    businessPlanChapters.FINANCIAL_PLAN.financialPerformance.profitability;

  const { liquidityVariants, liquidityTable } = businessPlanChapters.FINANCIAL_PLAN.financialPerformance.liquidity;

  const { efficiencyVariants, efficiencyTable } = businessPlanChapters.FINANCIAL_PLAN.financialPerformance.efficiency;

  const { solvencyVariants, solvencyTable } = businessPlanChapters.FINANCIAL_PLAN.financialPerformance.solvency;

  const openingBalanceSheetData = businessPlanChapters.FINANCIAL_PLAN.openingBalanceSheets;
  const shouldRenderRestRatios = shouldRenderFinancialData(openingBalanceSheetData);

  const { currency, unit } = businessPlanChapters.GLOBAL;
  const symbol = currency?.symbol || '';

  const profitabilityRatios: IFinBlockTableRowItem[] = [
    {
      type: 'regularText',
      heading: `${isExamplePage ? 'Gross Margin, %' : t('pdfPages.mappings.financialPlan.profitabilityRatiosTable.grossMargin')}`,
      values: ['>30', ...Object.values(profitabilityTable[0][period]).map((value) => convertToNumber(value))],
    },
    {
      type: 'regularText',
      heading: `${isExamplePage ? 'Net Profit Margin, %' : t('pdfPages.mappings.financialPlan.profitabilityRatiosTable.netProfitMargin')}`,
      values: ['5-20', ...Object.values(profitabilityTable[1][period]).map((value) => convertToNumber(value))],
    },
    {
      type: 'regularText',
      heading: `${isExamplePage ? 'EBITDA' : t('pdfPages.mappings.financialPlan.profitabilityRatiosTable.ebitda')}, ${unit === 'single' ? symbol : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`) + ' ' + symbol}`,
      values: ['', ...Object.values(profitabilityTable[2][period]).map((value) => convertToNumber(value))],
    },
    {
      type: 'regularText',
      heading: `${isExamplePage ? 'Net Profit' : t('pdfPages.mappings.financialPlan.netProfit')}, ${unit === 'single' ? symbol : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`) + ' ' + symbol}`,
      values: ['', ...Object.values(profitabilityTotal[period]).map((value) => convertToNumber(value))],
    },
  ];
  const finalProfitabilityRatios: IFinBlockTableRowItem[] = [
    {
      type: 'boldBlackText',
      heading: isExamplePage
        ? 'Profitability ratios'
        : t('financialPlan.financialPerformance.profitability.tableDescription'),
    },
  ];

  profitabilityRatios.forEach((item) => {
    if (item.values?.slice(1).some(Boolean)) {
      finalProfitabilityRatios.push(item);
    }
  });

  const ratios: IFinBlockTableRowItem[] = [];

  if (finalProfitabilityRatios.length > 1) {
    ratios.push(...finalProfitabilityRatios);
  }

  const liquidityRatios: IFinBlockTableRowItem[] = [
    {
      type: 'regularText',
      heading: isExamplePage ? liquidityVariants[0] : getTranslatedInitialValue(liquidityVariants[0]),
      values: ['1.5-2', ...Object.values(liquidityTable[0][period]).map((value) => convertToNumber(value))],
    },
    {
      type: 'regularText',
      heading: isExamplePage ? liquidityVariants[1] : getTranslatedInitialValue(liquidityVariants[1]),
      values: ['>1', ...Object.values(liquidityTable[1][period]).map((value) => convertToNumber(value))],
    },
  ];
  const finalLiquidityRatios: IFinBlockTableRowItem[] = [
    {
      type: 'boldBlackText',
      heading: isExamplePage ? 'Liquidity ratios' : t('financialPlan.financialPerformance.liquidity.tableDescription'),
    },
  ];

  liquidityRatios.forEach((item) => {
    if (item.values?.slice(1).some(Boolean)) {
      finalLiquidityRatios.push(item);
    }
  });

  const efficiencyRatios: IFinBlockTableRowItem[] = [
    {
      type: 'regularText',
      heading: isExamplePage ? efficiencyVariants[0] : getTranslatedInitialValue(efficiencyVariants[0]),
      values: ['', ...Object.values(efficiencyTable[0][period]).map((value) => convertToNumber(value))],
    },
    {
      type: 'regularText',
      heading: isExamplePage ? efficiencyVariants[1] : getTranslatedInitialValue(efficiencyVariants[1]),
      values: ['>8.0', ...Object.values(efficiencyTable[1][period]).map((value) => convertToNumber(value))],
    },
    {
      type: 'regularText',
      heading: isExamplePage ? efficiencyVariants[2] : getTranslatedInitialValue(efficiencyVariants[2]),
      values: ['', ...Object.values(efficiencyTable[2][period]).map((value) => convertToNumber(value))],
    },
    {
      type: 'regularText',
      heading: isExamplePage ? efficiencyVariants[3] : getTranslatedInitialValue(efficiencyVariants[3]),
      values: ['', ...Object.values(efficiencyTable[3][period]).map((value) => convertToNumber(value))],
    },
  ];

  const finalEfficiencyRatios: IFinBlockTableRowItem[] = [
    {
      type: 'boldBlackText',
      heading: isExamplePage
        ? 'Efficiency ratios'
        : t('financialPlan.financialPerformance.efficiency.tableDescription'),
    },
  ];

  efficiencyRatios.forEach((item) => {
    if (item.values?.slice(1).some(Boolean)) {
      finalEfficiencyRatios.push(item);
    }
  });

  const solvencyRatios: IFinBlockTableRowItem[] = [
    {
      type: 'regularText',
      heading: isExamplePage ? solvencyVariants[0] : getTranslatedInitialValue(solvencyVariants[0]),
      values: ['<1', ...Object.values(solvencyTable[0][period]).map((value) => convertToNumber(value))],
    },
    {
      type: 'regularText',
      heading: isExamplePage ? solvencyVariants[1] : getTranslatedInitialValue(solvencyVariants[1]),
      values: ['1.5-2', ...Object.values(solvencyTable[1][period]).map((value) => convertToNumber(value))],
    },
    {
      type: 'regularText',
      heading: isExamplePage ? solvencyVariants[2] : getTranslatedInitialValue(solvencyVariants[2]),
      values: ['>1.5', ...Object.values(solvencyTable[2][period]).map((value) => convertToNumber(value))],
    },
    {
      type: 'regularText',
      heading: isExamplePage ? solvencyVariants[3] : getTranslatedInitialValue(solvencyVariants[3]),
      values: ['', ...Object.values(solvencyTable[3][period]).map((value) => convertToNumber(value))],
    },
    {
      type: 'regularText',
      heading: isExamplePage ? solvencyVariants[4] : getTranslatedInitialValue(solvencyVariants[4]),
      values: ['>3', ...Object.values(solvencyTable[4][period]).map((value) => convertToNumber(value))],
    },
  ];
  const finalSolvencyRatios: IFinBlockTableRowItem[] = [
    {
      type: 'boldBlackText',
      heading: isExamplePage ? 'Solvency ratios' : t('financialPlan.financialPerformance.solvency.tableDescription'),
    },
  ];

  solvencyRatios.forEach((item) => {
    if (item.values?.slice(1).some(Boolean)) {
      finalSolvencyRatios.push(item);
    }
  });

  const restRatios: IFinBlockTableRowItem[] = [];

  if (finalLiquidityRatios.length > 1 && businessPlanType !== BUSINESS_PLAN_TYPES.FOR_INVESTORS) {
    restRatios.push(...finalLiquidityRatios);
  }

  if (finalEfficiencyRatios.length > 1 && businessPlanType !== BUSINESS_PLAN_TYPES.FOR_INVESTORS) {
    restRatios.push(...finalEfficiencyRatios);
  }
  if (finalSolvencyRatios.length > 1 && businessPlanType !== BUSINESS_PLAN_TYPES.FOR_INVESTORS) {
    restRatios.push(...finalSolvencyRatios);
  }

  if (shouldRenderRestRatios) {
    ratios.push(...restRatios);
  }

  return {
    headers: [
      isExamplePage ? 'Ratios' : t('pdfPages.mappings.financialPlan.ratios'),
      isExamplePage ? 'Benchmark value' : t('pdfPages.mappings.financialPlan.benchMarkValue'),
      ...(period === 'monthValues' ? monthsHeaders : headers),
    ],
    rows: ratios,
  };
};

export const getProfitAndLossProjectionTableData = (
  businessPlanChapters: IBusinessToolForm,
  operatingExpensesLinkedFields: string[],
  isExamplePage: boolean,
): IFinBlockTableProps => {
  const { plannedProductsVolume, totalPlannedProductsVolume, otherIncomePlannedProductsVolume } =
    businessPlanChapters.FINANCIAL_PLAN.revenuePlan;

  const { unit, period, periodTableHeadings, currency, products } = businessPlanChapters.GLOBAL;
  const symbol = currency?.symbol ? currency.symbol : '';

  const headers = Object.values(periodTableHeadings[period]);

  const productsRows: IFinBlockTableRowItem[] =
    plannedProductsVolume
      ?.filter((item) => item && period in item)
      ?.map((item, index) => ({
        type: 'regularText',
        heading: products?.[index]?.name || '',
        values: getFormattedTableNumberValues(item[period]),
      })) || [];

  const {
    netIncome: { financialIncome, financialExpenses, nonOperatingIncome, nonOperatingExpenses, incomeTax, netIncome },
    netSales: { grossProfit, costOfSales },
    operatingExpenses: {
      totalOperatingExpenses,
      rAndD,
      otherOperatingExpenses,
      generalAndAdministrative,
      salesAndMarketing,
    },
  } = businessPlanChapters.FINANCIAL_PLAN.profitAndLossProjection;
  const expenses = [rAndD, salesAndMarketing, generalAndAdministrative, otherOperatingExpenses];

  const operatingExpensesRows: IFinBlockTableRowItem[] = operatingExpensesLinkedFields.map((variant, index) => ({
    type: 'regularText' as RowType,
    heading: variant,
    values: getFormattedTableNumberValues(expenses[index][period]),
  }));

  return {
    rows: [
      {
        type: 'boldBlackText' as RowType,
        heading: isExamplePage ? 'Net sales/revenue' : t('pdfPages.mappings.financialPlan.netSalesRevenue'),
        values: getFormattedTableNumberValues(totalPlannedProductsVolume[period]),
      },
      ...productsRows,
      {
        type: 'regularText' as RowType,
        heading: isExamplePage ? 'Other income' : t('pdfPages.mappings.financialPlan.otherIncome'),
        values: getFormattedTableNumberValues(otherIncomePlannedProductsVolume[0]?.[period]),
      },
      {
        type: 'regularText' as RowType,
        heading: isExamplePage
          ? 'Cost of sales of products/services'
          : t('financialPlan.plannedExpenses.costOfSales.expectedRowTitle'),
        values: getFormattedTableNumberValues(costOfSales[period]),
      },
      {
        type: 'boldGreenText' as RowType,
        heading: isExamplePage ? 'Gross profit' : t('pdfPages.mappings.financialPlan.grossProfit'),
        values: getFormattedTableNumberValues(grossProfit[period]),
      },
      {
        type: 'regularText' as RowType,
        heading: isExamplePage ? 'Operating expenses:' : t('pdfPages.mappings.financialPlan.operatingExpences'),
      },
      ...operatingExpensesRows,
      {
        type: 'boldBlackText' as RowType,
        heading: isExamplePage
          ? 'Total operating expenses'
          : t('financialPlan.profitAndLossProjection.operatingExpenses.totalOperatingExpenses'),
        values: getFormattedTableNumberValues(totalOperatingExpenses[period]),
      },
      {
        type: 'regularText' as RowType,
        heading: isExamplePage
          ? 'Financial income'
          : t('financialPlan.profitAndLossProjection.netIncome.financialIncome'),
        values: getFormattedTableNumberValues(financialIncome[0][period]),
      },
      {
        type: 'regularText' as RowType,
        heading: isExamplePage
          ? 'Financial expenses'
          : t('financialPlan.plannedExpenses.monthlyExpenses.financialExpenses'),
        values: getFormattedTableNumberValues(financialExpenses[period]),
      },
      {
        type: 'regularText' as RowType,
        heading: isExamplePage
          ? 'Non-operating income'
          : t('financialPlan.profitAndLossProjection.netIncome.nonOperatingIncome'),
        values: getFormattedTableNumberValues(nonOperatingIncome[0][period]),
      },
      {
        type: 'regularText' as RowType,
        heading: isExamplePage
          ? 'Non-operating expenses'
          : t('financialPlan.plannedExpenses.monthlyExpenses.nonOperatingExpenses'),
        values: getFormattedTableNumberValues(nonOperatingExpenses[period]),
      },
      {
        type: 'regularText' as RowType,
        heading: isExamplePage ? 'Income tax' : t('financialPlan.profitAndLossProjection.netIncome.incomeTax'),
        values: getFormattedTableNumberValues(incomeTax[period]),
      },
      {
        type: 'boldGreenText' as RowType,
        heading: isExamplePage
          ? 'Net income/profit for the period'
          : t('financialPlan.projectedCashFlow.inDirect.netIncomeProfitForPeriod'),
        values: getFormattedTableNumberValues(netIncome[period]),
      },
    ].filter((row) => (row?.values ? row.values.some((value) => value !== 0) : true)),

    unit: `${symbol} ${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
    headers,
  };
};

export const getProfitAndLossChartData = (
  businessPlanChapters: IBusinessToolForm,
  isExamplePage: boolean,
): IChartWithBars => {
  const { totalPlannedProductsVolume } = businessPlanChapters.FINANCIAL_PLAN.revenuePlan;
  const {
    netIncome: { netIncome },
  } = businessPlanChapters.FINANCIAL_PLAN.profitAndLossProjection;

  const colors = getColorStylesByRecord(colorIndexesProfitAndLoss);

  const { unit, period, periodTableHeadings, currency } = businessPlanChapters.GLOBAL;
  const symbol = currency?.symbol ? currency.symbol : '';

  const headers = Object.values(periodTableHeadings[period]);

  const netSalesValues = Object.values(totalPlannedProductsVolume[period]);
  const netIncomeProfitValues = Object.values(netIncome[period]);
  const datasetsLabels = [
    isExamplePage ? 'Net sales/revenue' : t('pdfPages.mappings.financialPlan.netSalesRevenue'),
    isExamplePage ? 'Net profit' : t('pdfPages.mappings.financialPlan.netProfit'),
  ];

  const data = [netSalesValues, netIncomeProfitValues];

  return {
    title: '',
    datasetsLabels,
    data,
    unit: `${symbol} ${unit === 'single' ? '' : isExamplePage ? getDimensionalityForExamplePage(unit) : t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`)}`,
    labels: headers,
    colors,
  };
};
